import { type AppDispatch, type AppThunk } from "features/Application/globaltypes/redux";
import { cloneDeep, isNil } from "lodash";
import integrationsDataService from "../../../services/integrationsDataService";
import { type BaseIntegrationConfigs, IntegrationTypes, type MsGraphIntegrationConfigs } from "../../types";
import { type SfIntegrationConfigs } from "../../types/successFactors";
import { fetchBegin, fetchFailure, fetchSuccess, triggerSync } from "../slices/integrationDetailsSlice";
import { defaultRule } from "../../Configuration/MsGraphIntegrationFilterBuilder/helpers";
import enrollmentRulesUtils from "../../../../../utils/enrollmentRulesUtils";
import isEqual from "lodash/isEqual";

export const triggerMsGraphSync =
  (moboId?: string): AppThunk =>
  async (dispatch, _) => {
    try {
      await integrationsDataService.triggerMsSync(moboId);
      dispatch(triggerSync());
    } catch {}
  };

export const fetchIntegrationConfigsAction =
  (type: IntegrationTypes, moboId?: string) => async (dispatch: AppDispatch) => {
    dispatch(fetchBegin());
    try {
      const result = (await integrationsDataService.getIntegrationConfigs(type, moboId)).data;
      const mapper = mapFetched(type);
      dispatch(fetchSuccess(mapper(result)));
    } catch (error) {
      dispatch(fetchFailure(error as Error));
    }
  };
export const updateIntegrationConfigsAction =
  <T extends BaseIntegrationConfigs>(type: IntegrationTypes, configs: T, moboId?: string) =>
  async (dispatch: AppDispatch) => {
    dispatch(fetchBegin());
    try {
      const mapper = mapUpdated(type);
      const mappedConfigs = mapper(configs);

      await integrationsDataService.updateIntegrationConfigs(type, mappedConfigs, moboId);
      dispatch(fetchSuccess(configs));
    } catch (error) {
      dispatch(fetchFailure(error as Error));
    }
  };

const mapFetched = (type: IntegrationTypes) => (config: BaseIntegrationConfigs) => {
  const map: {
    [key in IntegrationTypes]?: (cfg: BaseIntegrationConfigs) => BaseIntegrationConfigs;
  } = {
    [IntegrationTypes.SuccessFactors]: (cfg: BaseIntegrationConfigs) => {
      const c = cfg as SfIntegrationConfigs;
      c.isHistoryEnabled = !isNil(c.completionHistorySettings);
      return c;
    },
    [IntegrationTypes.MsGraph]: (cfg: BaseIntegrationConfigs) => {
      const c = cfg as MsGraphIntegrationConfigs;
      if (!c.rules?.length) c.rules = [defaultRule];

      c.rules = enrollmentRulesUtils.mapRulesToLocalFormat(c.rules);
      return c;
    },
  };
  return map[type]?.(cloneDeep(config)) ?? config;
};

const mapUpdated = (type: IntegrationTypes) => (config: BaseIntegrationConfigs) => {
  const map: {
    [key in IntegrationTypes]?: (cfg: BaseIntegrationConfigs) => BaseIntegrationConfigs;
  } = {
    [IntegrationTypes.SuccessFactors]: (cfg: BaseIntegrationConfigs) => {
      const c = cfg as SfIntegrationConfigs;
      c.completionHistorySettings = c.isHistoryEnabled ? c.completionHistorySettings : null;
      return c;
    },
    [IntegrationTypes.MsGraph]: (cfg: BaseIntegrationConfigs) => {
      const c = cfg as MsGraphIntegrationConfigs;

      if (isEqual(c.rules, [defaultRule])) delete c.rules;

      if (c.rules?.length) {
        c.rules = enrollmentRulesUtils.mapRulesToSeverFormat(c.rules);
      }
      return c;
    },
  };
  return map[type]?.(cloneDeep(config)) ?? config;
};
